import { dsThemeContract } from './generated/contract.css';

/**
 * Theme-agnostic token contract for Design System consumers.
 *
 * Use these variables inside `ds-components` styles. Do not branch on HR/BO in
 * component code: theme selection must happen at app root via theme classes.
 *
 * @example
 * ```ts
 * import { style } from '@vanilla-extract/css';
 * import { componentSpecificTokenVars } from '@tundr/ds-tokens';
 *
 * export const buttonPrimary = style({
 *   backgroundColor: componentSpecificTokenVars.button.primary.default,
 *   borderRadius: '10px',
 * });
 * ```
 */
export const tokens = dsThemeContract;

/**
 * Base token variables (colors, scales).
 *
 * @example
 * ```ts
 * import { style } from '@vanilla-extract/css';
 * import { baseTokenVars } from '@tundr/ds-tokens';
 *
 * export const title = style({
 *   color: baseTokenVars.color.grey['900'],
 * });
 * ```
 */
export const baseTokenVars = dsThemeContract.base;

/**
 * Semantic token variables.
 *
 * @example
 * ```ts
 * import { style } from '@vanilla-extract/css';
 * import { semanticTokenVars } from '@tundr/ds-tokens';
 *
 * export const card = style({
 *   backgroundColor: semanticTokenVars.surface.background.neutral.primary,
 *   color: semanticTokenVars.content.primary.default,
 * });
 * ```
 */
export const semanticTokenVars = dsThemeContract.semantic;

/**
 * Responsive token variables (font-size, line-height, spacing).
 *
 * @example
 * ```ts
 * import { style } from '@vanilla-extract/css';
 * import { responsiveTokenVars } from '@tundr/ds-tokens';
 *
 * export const stack = style({
 *   gap: responsiveTokenVars.spacing.md,
 * });
 * ```
 */
export const responsiveTokenVars = dsThemeContract.responsive;

/**
 * Component-specific token variables used by DS components.
 *
 * @example
 * ```ts
 * import { style } from '@vanilla-extract/css';
 * import { componentSpecificTokenVars } from '@tundr/ds-tokens';
 *
 * export const badge = style({
 *   backgroundColor: componentSpecificTokenVars.badge.neutral,
 * });
 * ```
 */
export const componentSpecificTokenVars = dsThemeContract.componentSpecific;

export const elevationTokenVars = dsThemeContract.elevation;

const { shadowMd, shadowMdLeft, shadowMdRight, shadowMdTop } =
  dsThemeContract.elevation;

const toBoxShadow = ({ x, y }: { x: string; y: string }): string =>
  `${x} ${y} ${shadowMd.blur} ${shadowMd.spread} ${shadowMd.color}`;

export const shadowVars = {
  md: toBoxShadow(shadowMd),
  mdLeft: toBoxShadow(shadowMdLeft),
  mdRight: toBoxShadow(shadowMdRight),
  mdTop: toBoxShadow(shadowMdTop),
} as const;

export type TokensContract = typeof tokens;
export type ComponentSpecificTokenVars = typeof componentSpecificTokenVars;
